import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { BASE_URL } from "../../../../data/BaseUrl";
import "../../../../styles/trainer-report/staffing-goals.css";

const MAX_FETCH_LIMIT = 9999;
const TRAINER_TARGET  = 4;   // ideal trainers per gym per day
const RECEP_TARGET    = 2;   // ideal receptionists (sales execs) per gym per day

// ── Week window helpers (same pattern as DeploymentMatrixTab) ────────────────
const buildWindows = (year, month) => {
  const daysInMonth = moment(`${year}-${String(month).padStart(2, "0")}`, "YYYY-MM").daysInMonth();
  const windows = [];
  let start = 1;
  while (start <= daysInMonth) {
    windows.push({ start, end: Math.min(start + 6, daysInMonth) });
    start += 7;
  }
  return windows;
};

const getWindowDates = (year, month, winStart, winEnd) => {
  const dates = [];
  for (let d = winStart; d <= winEnd; d++) {
    dates.push(
      moment(
        `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        "YYYY-MM-DD"
      ).format("YYYY-MM-DD")
    );
  }
  return dates;
};

// ── Cell colour logic ────────────────────────────────────────────────────────
const getCellClass = (tp, rp, isFuture) => {
  if (isFuture)                               return "sgt-cell--future";
  if (tp === 0 && rp === 0)                   return "sgt-cell--red";
  if (tp >= TRAINER_TARGET && rp >= RECEP_TARGET) return "sgt-cell--green";
  return "sgt-cell--yellow";
};

// ── KPI flag cards ───────────────────────────────────────────────────────────
const FlagCards = ({ stats, activeFilter, onFilter }) => {
  const cards = [
    {
      key:    "ideal",
      label:  "Ideal Met",
      value:  stats.idealMet,
      sub:    `gym-days with T≥${TRAINER_TARGET} & R≥${RECEP_TARGET}`,
      mod:    stats.idealMet > 0 ? "green" : "grey",
    },
    {
      key:    "partial",
      label:  "Partial",
      value:  stats.partial,
      sub:    "some staff, below target",
      mod:    stats.partial > 0 ? "yellow" : "grey",
    },
    {
      key:    "critical",
      label:  "Critical",
      value:  stats.critical,
      sub:    "zero staff present",
      mod:    stats.critical > 0 ? "red" : "grey",
    },
    {
      key:    "avg",
      label:  "Avg Trainer Cover",
      value:  `${stats.avgTrainerPct}%`,
      sub:    `network avg vs ${TRAINER_TARGET}-trainer target`,
      mod:    stats.avgTrainerPct >= 75 ? "green" : stats.avgTrainerPct >= 50 ? "yellow" : "red",
    },
    {
      key:    "avgR",
      label:  "Avg Sales Cover",
      value:  `${stats.avgRecepPct}%`,
      sub:    `network avg vs ${RECEP_TARGET}-sales target`,
      mod:    stats.avgRecepPct >= 75 ? "green" : stats.avgRecepPct >= 50 ? "yellow" : "red",
    },
  ];

  const MOD_CLASSES = { green: "sgt-flag--green", yellow: "sgt-flag--yellow", red: "sgt-flag--red", grey: "" };

  return (
    <div className="sgt-flags">
      {cards.map((c) => (
        <div
          key={c.key}
          className={`sgt-flag ${MOD_CLASSES[c.mod] || ""}${activeFilter === c.key ? " sgt-flag--active" : ""}${c.key !== "avg" && c.key !== "avgR" ? " sgt-flag--clickable" : ""}`}
          onClick={() => c.key !== "avg" && c.key !== "avgR" && onFilter(c.key)}
          role={c.key !== "avg" && c.key !== "avgR" ? "button" : undefined}
          tabIndex={c.key !== "avg" && c.key !== "avgR" ? 0 : undefined}
          onKeyDown={c.key !== "avg" && c.key !== "avgR"
            ? (e) => e.key === "Enter" && onFilter(c.key)
            : undefined}
          title={c.key !== "avg" && c.key !== "avgR" ? "Click to filter" : undefined}
        >
          <div className="sgt-flag__label">{c.label}</div>
          <div className="sgt-flag__value">{c.value}</div>
          <div className="sgt-flag__sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

// ── Drill-down modal when a cell is clicked ───────────────────────────────────
const GoalsDrillDown = ({ gymName, date, dayEntry, onClose }) => {
  if (!dayEntry) return null;
  const { trainersPresent, trainersAbsent, receptionistsPresent, receptionistsAbsent } = dayEntry;

  return (
    <div className="sgt-drill-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sgt-drill-panel" onClick={(e) => e.stopPropagation()}>
        <div className="sgt-drill-header">
          <div>
            <div className="sgt-drill-header__gym">{gymName}</div>
            <div className="sgt-drill-header__date">{moment(date).format("ddd D MMM YYYY")}</div>
          </div>
          <button type="button" className="sgt-drill-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="sgt-drill-body">
          {/* Trainers */}
          <div className="sgt-drill-section">
            <div className="sgt-drill-section__title">
              Trainers
              <span className="sgt-drill-section__target">Target {TRAINER_TARGET}</span>
            </div>
            {trainersPresent.length > 0 && trainersPresent.map((s) => (
              <div key={s.user_id || s.name} className="sgt-drill-row sgt-drill-row--present">
                <span className="sgt-drill-dot sgt-drill-dot--green" />
                {s.name || s.full_name || "—"}
              </div>
            ))}
            {trainersAbsent.length > 0 && trainersAbsent.map((s) => (
              <div key={s.user_id || s.name} className="sgt-drill-row sgt-drill-row--absent">
                <span className="sgt-drill-dot sgt-drill-dot--red" />
                {s.name || s.full_name || "—"}
                <span className="sgt-drill-badge sgt-drill-badge--absent">absent</span>
              </div>
            ))}
            {trainersPresent.length === 0 && trainersAbsent.length === 0 && (
              <div className="sgt-drill-empty">No trainer data</div>
            )}
          </div>
          {/* Sales Execs */}
          <div className="sgt-drill-section">
            <div className="sgt-drill-section__title">
              Sales Execs
              <span className="sgt-drill-section__target">Target {RECEP_TARGET}</span>
            </div>
            {receptionistsPresent.length > 0 && receptionistsPresent.map((s) => (
              <div key={s.user_id || s.name} className="sgt-drill-row sgt-drill-row--present">
                <span className="sgt-drill-dot sgt-drill-dot--green" />
                {s.name || s.full_name || "—"}
              </div>
            ))}
            {receptionistsAbsent.length > 0 && receptionistsAbsent.map((s) => (
              <div key={s.user_id || s.name} className="sgt-drill-row sgt-drill-row--absent">
                <span className="sgt-drill-dot sgt-drill-dot--red" />
                {s.name || s.full_name || "—"}
                <span className="sgt-drill-badge sgt-drill-badge--absent">absent</span>
              </div>
            ))}
            {receptionistsPresent.length === 0 && receptionistsAbsent.length === 0 && (
              <div className="sgt-drill-empty">No sales exec data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const StaffingGoalsTab = ({ gymFilter }) => {
  const now = moment();
  const [year,          setYear]          = useState(now.year());
  const [month,         setMonth]         = useState(now.month() + 1);
  const [windowIdx,     setWindowIdx]     = useState(0);
  const [dayData,       setDayData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [activeCell,    setActiveCell]    = useState(null); // { gymId, date }
  const [activeFilter,  setActiveFilter]  = useState(null); // "ideal"|"partial"|"critical"|null

  const token = useSelector((s) => s.auth?.user?.token);

  const windows = useMemo(() => buildWindows(year, month), [year, month]);

  // Clamp windowIdx on month change, reset to current week on mount
  useEffect(() => {
    setWindowIdx((i) => Math.min(i, windows.length - 1));
    setActiveCell(null);
    setActiveFilter(null);
  }, [year, month, windows.length]);

  useEffect(() => {
    const today = moment();
    if (today.year() === year && today.month() + 1 === month) {
      const d = today.date();
      const idx = windows.findIndex((w) => w.start <= d && w.end >= d);
      if (idx >= 0) setWindowIdx(idx);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setActiveCell(null); }, [gymFilter]);

  const currentWindow = windows[windowIdx] || windows[0];
  const windowDates = useMemo(
    () => currentWindow ? getWindowDates(year, month, currentWindow.start, currentWindow.end) : [],
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, month, currentWindow?.start, currentWindow?.end]
  );

  const dateFrom = windowDates[0] || "";
  const dateTo   = windowDates[windowDates.length - 1] || "";

  // One parallel request per day — same pattern as DeploymentMatrixTab
  useEffect(() => {
    if (!token || windowDates.length === 0) return;
    const controller = new AbortController();

    const fetchDay = async (date) => {
      const params = new URLSearchParams({ date_from: date, date_to: date, limit: MAX_FETCH_LIMIT, page: 1 });
      if (gymFilter?.value) params.set("gym_id", gymFilter.value);
      const res = await fetch(`${BASE_URL}/staff/report/v3?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const records = Array.isArray(json.data)    ? json.data
                    : Array.isArray(json.result)  ? json.result
                    : Array.isArray(json.records) ? json.records
                    : [];
      return { date, records };
    };

    const fetchAll = async () => {
      setDayData([]);
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(windowDates.map(fetchDay));
        setDayData(results);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load staffing data. Please retry.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchAll();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, token, reloadTrigger, gymFilter]);

  /**
   * Build gymDateMap:
   *  { [gymId]: { gymName, dates: { [date]: { trainersPresent[], trainersAbsent[],
   *                                            receptionistsPresent[], receptionistsAbsent[] } } } }
   */
  const gymDateMap = useMemo(() => {
    const map = {};
    dayData.forEach(({ date, records }) => {
      records.forEach((staff) => {
        const { gym_id, gym_name, account_type } = staff;
        if (!gym_id) return;
        if (!map[gym_id]) map[gym_id] = { gymName: gym_name, dates: {} };
        if (!map[gym_id].dates[date]) {
          map[gym_id].dates[date] = {
            trainersPresent: [], trainersAbsent: [],
            receptionistsPresent: [], receptionistsAbsent: [],
          };
        }
        const slot = map[gym_id].dates[date];

        (staff.shift_details || []).forEach((shift) => {
          const att       = shift.attendance_details || {};
          const isPresent = att.checkin_status === "Checked-In" || att.checkout_status === "checked-out";
          const isOnLeave = att.status_value === "on_leave";
          const isDayOff  = att.status_value === "day_off";
          const isExpected = !isOnLeave && !isDayOff;
          const entry = { user_id: staff.user_id, name: staff.name || staff.full_name };

          if (account_type === "trainer") {
            if (isPresent)        slot.trainersPresent.push(entry);
            else if (isExpected)  slot.trainersAbsent.push(entry);
          } else if (account_type === "receptionist") {
            if (isPresent)        slot.receptionistsPresent.push(entry);
            else if (isExpected)  slot.receptionistsAbsent.push(entry);
          }
        });
      });
    });
    return map;
  }, [dayData]);

  // KPI stats across the window (past days only)
  const stats = useMemo(() => {
    const todayStr = moment().format("YYYY-MM-DD");
    let idealMet = 0, partial = 0, critical = 0;
    let trainerPctSum = 0, recepPctSum = 0, slots = 0;

    Object.values(gymDateMap).forEach(({ dates }) => {
      windowDates.forEach((date) => {
        if (date > todayStr) return;
        const day = dates[date];
        if (!day) return;
        const tp = day.trainersPresent.length;
        const rp = day.receptionistsPresent.length;
        slots++;
        if (tp === 0 && rp === 0) { critical++; }
        else if (tp >= TRAINER_TARGET && rp >= RECEP_TARGET) { idealMet++; }
        else { partial++; }
        trainerPctSum += Math.min((tp / TRAINER_TARGET) * 100, 100);
        recepPctSum   += Math.min((rp / RECEP_TARGET)   * 100, 100);
      });
    });
    return {
      idealMet,
      partial,
      critical,
      avgTrainerPct: slots > 0 ? Math.round(trainerPctSum / slots) : 0,
      avgRecepPct:   slots > 0 ? Math.round(recepPctSum   / slots) : 0,
    };
  }, [gymDateMap, windowDates]);

  // Apply flag filter
  const filteredGymDateMap = useMemo(() => {
    if (!activeFilter) return gymDateMap;
    const todayStr = moment().format("YYYY-MM-DD");
    return Object.fromEntries(
      Object.entries(gymDateMap).filter(([, { dates }]) =>
        windowDates.some((date) => {
          if (date > todayStr) return false;
          const day = dates[date];
          if (!day) return false;
          const tp = day.trainersPresent.length;
          const rp = day.receptionistsPresent.length;
          if (activeFilter === "critical") return tp === 0 && rp === 0;
          if (activeFilter === "ideal")    return tp >= TRAINER_TARGET && rp >= RECEP_TARGET;
          if (activeFilter === "partial")  return (tp > 0 || rp > 0) && !(tp >= TRAINER_TARGET && rp >= RECEP_TARGET);
          return true;
        })
      )
    );
  }, [gymDateMap, activeFilter, windowDates]);

  const gymList = useMemo(
    () => Object.entries(filteredGymDateMap).sort((a, b) => a[1].gymName.localeCompare(b[1].gymName)),
    [filteredGymDateMap]
  );

  const handleFilterToggle = useCallback((key) => {
    setActiveFilter((prev) => (prev === key ? null : key));
    setActiveCell(null);
  }, []);

  const handleCellClick = useCallback((gymId, date) => {
    setActiveCell((prev) => prev?.gymId === gymId && prev?.date === date ? null : { gymId, date });
  }, []);

  const goToPrevMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").subtract(1, "month");
    setYear(m.year()); setMonth(m.month() + 1); setWindowIdx(0);
  };
  const goToNextMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").add(1, "month");
    if (!m.isAfter(moment(), "month")) { setYear(m.year()); setMonth(m.month() + 1); setWindowIdx(0); }
  };

  const isNextDisabled = moment(`${year}-${month}`, "YYYY-M").isSameOrAfter(moment(), "month");
  const monthLabel     = moment(`${year}-${String(month).padStart(2, "0")}`, "YYYY-MM").format("MMMM YYYY");
  const todayStr       = moment().format("YYYY-MM-DD");

  // Active drill-down data
  const drillEntry = activeCell
    ? filteredGymDateMap[activeCell.gymId]?.dates?.[activeCell.date] ?? null
    : null;
  const drillGymName = activeCell ? filteredGymDateMap[activeCell.gymId]?.gymName : "";

  return (
    <div className="sdt-container">
      {/* ── KPI cards ────────────────────────────────── */}
      <FlagCards stats={stats} activeFilter={activeFilter} onFilter={handleFilterToggle} />

      {/* ── Week navigator ───────────────────────────── */}
      <div className="sdm-week-nav">
        <button type="button" className="sdm-week-nav__arrow" onClick={goToPrevMonth} aria-label="Previous month">&#8592;</button>
        <span className="sdm-week-nav__month">{monthLabel}</span>
        <div className="sdm-week-nav__windows">
          {windows.map((w, i) => (
            <button
              key={i}
              type="button"
              className={`sdm-week-nav__btn${windowIdx === i ? " sdm-week-nav__btn--active" : ""}`}
              onClick={() => { setWindowIdx(i); setActiveCell(null); }}
            >
              {w.start}–{w.end}
            </button>
          ))}
        </div>
        <button type="button" className="sdm-week-nav__arrow" onClick={goToNextMonth} disabled={isNextDisabled} aria-label="Next month">&#8594;</button>
        <button
          type="button"
          className="sdt-date-nav__reload"
          onClick={() => setReloadTrigger((n) => n + 1)}
          style={{ marginLeft: "auto" }}
          aria-label="Reload"
        >
          ↻ Reload
        </button>
      </div>

      {/* Active filter banner */}
      {activeFilter && (
        <div className="sdt-filter-banner">
          Showing: <strong>
            {activeFilter === "ideal" ? "Ideal Met (T≥4 & R≥2)" :
             activeFilter === "partial" ? "Partial Coverage" : "Critical (Zero Staff)"}
          </strong>
          <button type="button" className="sdt-filter-banner__clear" onClick={() => setActiveFilter(null)}>✕ Clear</button>
        </div>
      )}

      {/* ── Legend ───────────────────────────────────── */}
      <div className="sgt-legend">
        <span className="sgt-legend__item sgt-legend__item--green">Ideal (T:{TRAINER_TARGET}+ R:{RECEP_TARGET}+)</span>
        <span className="sgt-legend__item sgt-legend__item--yellow">Partial</span>
        <span className="sgt-legend__item sgt-legend__item--red">Critical (0 staff)</span>
        <span className="sgt-legend__item sgt-legend__item--future">Future</span>
      </div>

      {/* ── Error ────────────────────────────────────── */}
      {error && (
        <div className="sdm-error">
          {error}{" "}
          <button type="button" className="sdm-error__retry" onClick={() => setReloadTrigger((n) => n + 1)}>Retry</button>
        </div>
      )}

      {/* ── Grid ─────────────────────────────────────── */}
      {!error && (
        <div className="sgt-table-wrap">
          {loading ? (
            <div className="sgt-loading">Loading staffing data…</div>
          ) : gymList.length === 0 ? (
            <div className="sdm-empty">No gym data found for this window.</div>
          ) : (
            <table className="sgt-table" role="grid">
              <thead>
                <tr>
                  <th className="sgt-th sgt-th--gym">Gym</th>
                  {windowDates.map((d) => (
                    <th key={d} className={`sgt-th${d === todayStr ? " sgt-th--today" : ""}`}>
                      <div className="sgt-th__day">{moment(d).format("ddd")}</div>
                      <div className="sgt-th__date">{moment(d).format("D")}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gymList.map(([gymId, { gymName, dates }]) => (
                  <tr key={gymId} className="sgt-row">
                    <td className="sgt-td--gym" title={gymName}>
                      <span className="sgt-gym-name">{gymName}</span>
                    </td>
                    {windowDates.map((date) => {
                      const day       = dates[date];
                      const isFuture  = date > todayStr;
                      const tp        = day ? day.trainersPresent.length     : 0;
                      const rp        = day ? day.receptionistsPresent.length : 0;
                      const cellClass = getCellClass(tp, rp, isFuture);
                      const isActive  = activeCell?.gymId === gymId && activeCell?.date === date;
                      return (
                        <td
                          key={date}
                          className={`sgt-cell ${cellClass}${isActive ? " sgt-cell--active" : ""}${isFuture ? "" : " sgt-cell--clickable"}`}
                          onClick={() => !isFuture && handleCellClick(gymId, date)}
                          role={isFuture ? undefined : "button"}
                          tabIndex={isFuture ? undefined : 0}
                          onKeyDown={isFuture ? undefined : (e) => e.key === "Enter" && handleCellClick(gymId, date)}
                          aria-pressed={isActive}
                          title={isFuture ? "Future date" : `T:${tp}/${TRAINER_TARGET} R:${rp}/${RECEP_TARGET} — click for details`}
                        >
                          {!isFuture && day ? (
                            <>
                              <span className="sgt-cell__t">T:{tp}</span>
                              <span className="sgt-cell__r">R:{rp}</span>
                            </>
                          ) : (
                            <span className="sgt-cell__dash">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Drill-down modal ──────────────────────────── */}
      {activeCell && drillEntry && (
        <GoalsDrillDown
          gymName={drillGymName}
          date={activeCell.date}
          dayEntry={drillEntry}
          onClose={() => setActiveCell(null)}
        />
      )}
    </div>
  );
};

export default StaffingGoalsTab;
